import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
export const GALLERY_FETCH_LIMIT = 48;
export const GALLERY_RENDER_BATCH = 24;

function buildFeedUrl({ offset, categoryId, searchQuery }) {
  const params = new URLSearchParams({
    paginate: '1',
    offset: String(offset),
    limit: String(GALLERY_FETCH_LIMIT),
    category: categoryId || 'all',
  });
  const q = (searchQuery || '').trim();
  if (q) params.set('q', q);
  return `${API_BASE}/social-feed?${params.toString()}`;
}

/**
 * Global paginated gallery feed + progressive DOM render (infinite scroll).
 */
export function useGalleryFeed({ categoryId, searchQuery, enabled = true }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [renderCount, setRenderCount] = useState(GALLERY_RENDER_BATCH);

  const offsetRef = useRef(0);
  const requestIdRef = useRef(0);
  const itemsRef = useRef([]);
  const hasMoreRef = useRef(false);
  const renderCountRef = useRef(GALLERY_RENDER_BATCH);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    renderCountRef.current = renderCount;
  }, [renderCount]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const revealMoreInDom = useCallback(() => {
    if (renderCountRef.current >= itemsRef.current.length) return false;
    const before = renderCountRef.current;
    setRenderCount((n) => {
      const next = Math.min(n + GALLERY_RENDER_BATCH, itemsRef.current.length);
      renderCountRef.current = next;
      return next;
    });
    return renderCountRef.current > before;
  }, []);

  const resetAndLoad = useCallback(async () => {
    if (!enabled) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    offsetRef.current = 0;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    setItems([]);
    setRenderCount(GALLERY_RENDER_BATCH);

    try {
      const res = await fetch(buildFeedUrl({ offset: 0, categoryId, searchQuery: debouncedSearch }));
      const data = res.ok ? await res.json() : { items: [], total: 0, hasMore: false };
      if (requestId !== requestIdRef.current) return;

      const nextItems = Array.isArray(data.items) ? data.items : [];
      itemsRef.current = nextItems;
      hasMoreRef.current = Boolean(data.hasMore);
      renderCountRef.current = GALLERY_RENDER_BATCH;
      setItems(nextItems);
      setTotal(typeof data.total === 'number' ? data.total : nextItems.length);
      setHasMore(Boolean(data.hasMore));
      offsetRef.current = nextItems.length;
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(e);
      itemsRef.current = [];
      hasMoreRef.current = false;
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [enabled, categoryId, debouncedSearch]);

  useEffect(() => {
    resetAndLoad();
  }, [resetAndLoad]);

  const fetchNextPage = useCallback(async () => {
    if (!enabled || loadingMoreRef.current || !hasMoreRef.current) return false;

    const requestId = requestIdRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await fetch(
        buildFeedUrl({ offset: offsetRef.current, categoryId, searchQuery: debouncedSearch })
      );
      const data = res.ok ? await res.json() : { items: [], hasMore: false };
      if (requestId !== requestIdRef.current) return false;

      const page = Array.isArray(data.items) ? data.items : [];
      if (!page.length) {
        hasMoreRef.current = false;
        setHasMore(false);
        return false;
      }

      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        const merged = [...prev];
        for (const row of page) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            merged.push(row);
          }
        }
        itemsRef.current = merged;
        return merged;
      });
      if (typeof data.total === 'number') setTotal(data.total);
      hasMoreRef.current = Boolean(data.hasMore);
      setHasMore(Boolean(data.hasMore));
      offsetRef.current += page.length;
      return true;
    } catch {
      return false;
    } finally {
      if (requestId === requestIdRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [enabled, categoryId, debouncedSearch]);

  /** One infinite-scroll step: reveal buffered items or fetch the next API page. */
  const loadMoreStep = useCallback(async () => {
    if (!enabled || loadingRef.current || loadingMoreRef.current) return false;

    if (renderCountRef.current < itemsRef.current.length) {
      return revealMoreInDom();
    }

    if (!hasMoreRef.current) return false;

    const fetched = await fetchNextPage();
    if (!fetched) return false;
    return revealMoreInDom();
  }, [enabled, revealMoreInDom, fetchNextPage]);

  const showMoreInDom = revealMoreInDom;

  const loadMoreFromApi = fetchNextPage;

  const bumpRenderCount = useCallback((minCount) => {
    setRenderCount((n) => {
      const next = Math.max(n, minCount);
      renderCountRef.current = next;
      return next;
    });
  }, []);

  const ensureLoadedThroughIndex = useCallback(
    async (targetIndex) => {
      if (!enabled || targetIndex < 0) return;

      let guard = 0;
      while (guard < 80) {
        guard += 1;
        const len = itemsRef.current.length;
        const rc = renderCountRef.current;

        if (len > targetIndex && rc > targetIndex) return;

        if (rc < len) {
          revealMoreInDom();
          await new Promise((r) => setTimeout(r, 0));
          continue;
        }

        if (hasMoreRef.current) {
          await fetchNextPage();
          await new Promise((r) => setTimeout(r, 0));
          continue;
        }

        bumpRenderCount(targetIndex + GALLERY_RENDER_BATCH);
        return;
      }

      bumpRenderCount(targetIndex + GALLERY_RENDER_BATCH);
    },
    [enabled, revealMoreInDom, fetchNextPage, bumpRenderCount]
  );

  const visibleItems = items.slice(0, renderCount);
  const canRenderMore = renderCount < items.length;
  const needsFetchMore = !canRenderMore && hasMore;

  return {
    items,
    visibleItems,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    canRenderMore,
    needsFetchMore,
    loadMoreStep,
    loadMoreFromApi,
    showMoreInDom,
    ensureLoadedThroughIndex,
    bumpRenderCount,
    renderCount,
  };
}

export async function fetchGalleryMeta({ categoryId = 'all', searchQuery = '' } = {}) {
  const params = new URLSearchParams({ category: categoryId || 'all' });
  const q = (searchQuery || '').trim();
  if (q) params.set('q', q);
  const res = await fetch(`${API_BASE}/social-feed/meta?${params.toString()}`);
  if (!res.ok) {
    return { configured: false, monthSteps: [], specialMonths: [], monthAnchors: [], total: 0 };
  }
  return res.json();
}
